import { useCallback, useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, WifiOff } from 'lucide-react';
import { connector } from '@/lib/auth';
import {
  getPendingCount,
  flushVentaQueue,
  flushAnularQueue,
  flushCajaQueue,
  flushAbonosQueue,
  getPendingAnularCount,
  getPendingCajaCount,
  getPendingAbonoCount,
  getProblemaCount,
  SYNC_REFRESH_EVENT,
} from '@/lib/sync-queue';
import { Chip, type ChipTone } from './Chip';
import { PendingSalesPanel } from './PendingSalesPanel';

type SyncStatus = 'sincronizado' | 'pendiente' | 'offline' | 'sincronizando' | 'error' | 'problema';

function getSupabaseRpcClient(accessToken?: string | null) {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!url || !key) return null;
  // Pass the cashier JWT so RPCs (caja / anular) run as the authenticated user.
  // Creating a bare anon client often has no session in the POS (auth lives in
  // connector's client), so flushes would fail under RLS / current_org_id().
  if (accessToken) {
    return createClient(url, key, {
      global: { headers: { Authorization: `Bearer ${accessToken}` } },
    });
  }
  return createClient(url, key);
}

type IndicatorMeta = {
  Icon: typeof CheckCircle2;
  tone: ChipTone;
  pulse?: boolean;
  spin?: boolean;
};

const statusMeta: Record<SyncStatus, IndicatorMeta> = {
  sincronizado: {
    Icon: CheckCircle2,
    tone: 'success',
  },
  pendiente: {
    // "n pending" is in-progress — neutral + motion, per DESIGN.md.
    Icon: Loader2,
    tone: 'in-progress',
    pulse: true,
  },
  sincronizando: {
    Icon: Loader2,
    tone: 'in-progress',
    spin: true,
  },
  offline: {
    Icon: WifiOff,
    tone: 'neutral',
  },
  error: {
    Icon: AlertCircle,
    tone: 'danger',
  },
  problema: {
    Icon: AlertTriangle,
    tone: 'danger',
  },
};

export function SyncIndicator() {
  const [status, setStatus] = useState<SyncStatus>('sincronizado');
  const [pending, setPending] = useState(0);
  const [problemas, setProblemas] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);

  const refresh = useCallback(() => {
    window.dispatchEvent(new Event(SYNC_REFRESH_EVENT));
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        if (!navigator.onLine) {
          if (!cancelled) {
            setStatus('offline');
            setPending(0);
            setProblemas(0);
          }
          return;
        }

        const problemasCount = await getProblemaCount();
        const ventasCount = await getPendingCount();
        const anularCount = await getPendingAnularCount();
        const cajaCount = await getPendingCajaCount();
        const abonoCount = await getPendingAbonoCount();
        const count = ventasCount + anularCount + cajaCount + abonoCount;
        if (!cancelled) {
          setPending(count);
          setProblemas(problemasCount);
          if (problemasCount > 0) setStatus('problema');
          else setStatus(count > 0 ? 'pendiente' : 'sincronizado');
        }
      } catch (err) {
        console.warn('[SyncIndicator] check failed:', err);
        if (!cancelled) setStatus('error');
      }
    }

    async function flushIfPending() {
      if (!navigator.onLine) return;
      const ventasCount = await getPendingCount();
      const anularCount = await getPendingAnularCount();
      const cajaCount = await getPendingCajaCount();
      const abonoCount = await getPendingAbonoCount();
      if (ventasCount === 0 && anularCount === 0 && cajaCount === 0 && abonoCount === 0) return;

      if (!cancelled) setStatus('sincronizando');
      try {
        let ok = 0;
        let fail = 0;

        const token = await connector.getAccessToken();
        if (ventasCount > 0) {
          if (token) {
            const r = await flushVentaQueue(token);
            ok += r.ok;
            fail += r.fail;
          }
        }

        const rpcClient = getSupabaseRpcClient(token);
        if (rpcClient && (anularCount > 0 || cajaCount > 0 || abonoCount > 0)) {
          const rpcWrapper = (fn: string, args: Record<string, unknown>) =>
            rpcClient.rpc(fn, args) as unknown as Promise<{
              data?: unknown;
              error?: { message?: string } | null;
            }>;
          if (anularCount > 0) {
            const r = await flushAnularQueue({ rpc: rpcWrapper });
            ok += r.ok;
            fail += r.fail;
          }
          if (cajaCount > 0) {
            const r = await flushCajaQueue({
              rpc: rpcWrapper,
              // Let YA_TIENE_SESION_ABIERTA recover by reading the live row.
              select: rpcClient as never,
            });
            ok += r.ok;
            fail += r.fail;
          }
          if (abonoCount > 0) {
            const r = await flushAbonosQueue({ rpc: rpcWrapper });
            ok += r.ok;
            fail += r.fail;
          }
        }

        if (!cancelled) {
          const problemasAfter = await getProblemaCount();
          const stillPending =
            (await getPendingCount()) +
            (await getPendingAnularCount()) +
            (await getPendingCajaCount()) +
            (await getPendingAbonoCount());
          setProblemas(problemasAfter);
          setPending(stillPending);
          if (problemasAfter > 0) setStatus('problema');
          else if (fail > 0 && ok === 0) setStatus('error');
          else if (stillPending > 0) setStatus('pendiente');
          else setStatus('sincronizado');
        }
      } catch (err) {
        console.warn('[SyncIndicator] flush fallo:', err);
        if (!cancelled) setStatus('error');
      }
    }

    const runCheck = () => {
      void check();
    };
    const runFlush = () => {
      void flushIfPending();
    };
    const onRefresh = () => {
      runCheck();
      runFlush();
    };

    runCheck();
    runFlush();
    // Safety net only: enqueue paths fire SYNC_REFRESH_EVENT for immediate flush.
    const interval = setInterval(() => {
      runCheck();
      runFlush();
    }, 15_000);
    const onOnline = () => {
      runCheck();
      runFlush();
    };
    const onOffline = () => setStatus('offline');
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener(SYNC_REFRESH_EVENT, onRefresh);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener(SYNC_REFRESH_EVENT, onRefresh);
    };
  }, []);

  const label =
    status === 'sincronizado'
      ? 'Sincronizado'
      : status === 'sincronizando'
        ? 'Sincronizando…'
        : status === 'pendiente'
          ? `${pending} pendiente${pending === 1 ? '' : 's'}`
          : status === 'problema'
            ? `${problemas} sin sincronizar`
            : status === 'offline'
              ? 'Offline'
              : 'Error de sincronización';

  const meta = statusMeta[status];
  const Icon = meta.Icon;
  const iconClass = meta.spin ? 'animate-spin' : meta.pulse ? 'animate-pulse' : '';

  const chip = (
    <Chip tone={meta.tone} pulse={meta.pulse}>
      <Icon className={`h-3.5 w-3.5 ${iconClass}`} aria-hidden="true" />
      <span role="status" aria-live="polite" aria-atomic="true">
        {label}
      </span>
    </Chip>
  );

  if (problemas === 0) return chip;

  return (
    <>
      <button
        type="button"
        onClick={() => setPanelOpen(true)}
        className="rounded-pill focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
        aria-label={`${problemas} ventas sin sincronizar. Abrir detalle`}
      >
        {chip}
      </button>
      <PendingSalesPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onReintentar={refresh}
      />
    </>
  );
}
